import {
  SimpleModal,
  Flex,
  InputSelectControl,
  Text,
  Primary13SemiBold,
  GreyGrey80
} from '@sequencehq/core-components'
import { FC, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { formatScheduleDateRange } from 'modules/Cube/utils/formatScheduleDateRange'
import {
  ScheduleChangeDateSelector,
  ScheduleChangeDateType
} from 'common/components/ScheduleChangeDateSelector/ScheduleChangeDateSelector'
import { useAmendableSchedules } from './useAmendableSchedules'
import { ScheduleAmendment } from './useApplyAmendmentToQuote'
import { useSelectedScheduleDates } from './useSelectedScheduleDates'

type Props = {
  onSubmit: (amendment: ScheduleAmendment) => Promise<boolean>
  onCancel: () => void
}

type AmendmentDate = {
  type: ScheduleChangeDateType
  customDate?: Date
}

export const SelectScheduleForAmendment: FC<Props> = ({
  onSubmit,
  onCancel
}) => {
  const cubeContext = useCubeContext()
  const customerId = cubeContext.queries.rawData.data.common.customerId
  const customerName =
    cubeContext.queries.rawData.data.customers[customerId]?.legalName

  const { schedules, isLoading } = useAmendableSchedules({ customerId })
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>()
  const [amendmentDate, setAmendmentDate] = useState<AmendmentDate>()

  const selectedSchedule = schedules.find(
    schedule => schedule.id === selectedScheduleId
  )
  const { scheduleDates, hasFuturePhases } =
    useSelectedScheduleDates(selectedSchedule)

  const firstScheduleId = schedules.at(0)?.id

  useEffect(() => {
    if (firstScheduleId) {
      setSelectedScheduleId(firstScheduleId)
    }
  }, [firstScheduleId])

  // Each schedule has its own dates, so switching schedule starts the choice again.
  useEffect(() => {
    if (hasFuturePhases === undefined) {
      return
    }

    setAmendmentDate({ type: hasFuturePhases ? 'customDate' : 'immediate' })
  }, [selectedScheduleId, hasFuturePhases])

  const hasSchedules = schedules.length > 0
  const hasNoEligibleSchedules = !isLoading && !hasSchedules

  const effectiveDate = match(amendmentDate?.type)
    .with('immediate', () => new Date())
    .with('nextBillingDate', () => scheduleDates.nextBillingDate ?? undefined)
    .with('customDate', () => amendmentDate?.customDate)
    .with(undefined, () => undefined)
    .exhaustive()

  return (
    <SimpleModal
      data-testid="quote.modals.selectScheduleForAmendment"
      title="Amend existing schedule"
      isOpen
      onClose={onCancel}
      submitButtonText="Continue"
      submitDisabled={!selectedSchedule || !effectiveDate}
      onSubmit={async () => {
        if (!selectedSchedule || !effectiveDate) {
          return false
        }

        return onSubmit({
          schedule: selectedSchedule,
          amendmentDate: effectiveDate
        })
      }}
    >
      <Flex flexDir="column" gap="24px">
        <Flex flexDir="column" gap="12px">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            Choose a schedule to amend
          </Text>
          <InputSelectControl
            data-testid="quote.modals.selectScheduleForAmendment.scheduleSelect"
            value={selectedScheduleId ?? ''}
            onChange={setSelectedScheduleId}
            disabled={!hasSchedules}
            placeholder={
              hasNoEligibleSchedules
                ? `No eligible schedules to amend for ${customerName ?? 'this customer'}`
                : 'Select a schedule'
            }
            justifyLabels="space-between"
            options={schedules.map(schedule => ({
              value: schedule.id,
              label: formatScheduleDateRange(schedule),
              secondaryLabel: schedule.prices.at(0)?.currency ?? ''
            }))}
          />
        </Flex>

        <Flex flexDir="column" gap="12px">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            When should the changes take effect?
          </Text>
          <ScheduleChangeDateSelector
            name="amendmentDateType"
            value={amendmentDate?.type}
            onChange={type => setAmendmentDate({ type })}
            customDate={amendmentDate?.customDate}
            onCustomDateChange={customDate =>
              setAmendmentDate({ type: 'customDate', customDate })
            }
            scheduleDates={scheduleDates}
            isDisabled={!selectedSchedule}
            disabledReason="Select a schedule to amend first."
          />
        </Flex>
      </Flex>
    </SimpleModal>
  )
}
